import React, { useState } from 'react';
import { Icons } from './Icons';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import { track } from '../utils/analytics';
import { buildInviteUrl } from '../utils/inviteLink';
import { byName } from '../utils/helpers';

const ROLES = [
  { value: 'viewer',      label: 'Viewer',      desc: 'Can view the tree but not make changes' },
  { value: 'contributor', label: 'Contributor', desc: 'Can edit their own direct family unit only' },
  { value: 'admin',       label: 'Admin',       desc: 'Can add, edit and delete family members' },
];

export default function MembersPanel({ onClose }) {
  const { treeMembers, memberDetails, pendingInvites, userRole, inviteMember, revokeInvite, updateMemberRole, removeMember, userTrees, activeTreeId, peopleCount } = useTree();
  const { people } = useTreePeople();
  const treeName = userTrees.find(t => t.treeId === activeTreeId)?.name || 'our Family Tree';
  const isOwner = userRole === 'owner';

  const [email, setEmail]                   = useState('');
  const [role, setRole]                     = useState('viewer');
  const [linkedPersonId, setLinkedPersonId] = useState('');
  const [sending, setSending]               = useState(false);
  const [feedback, setFeedback]             = useState(null); // { type: 'ok'|'err', text }
  const [lastInvite, setLastInvite]         = useState(null); // { email, subject, body } — kept so the message survives a mailto: that did nothing
  const [copied, setCopied]                 = useState('');

  const peopleList = Object.values(people).sort(byName);

  const members = Object.entries(treeMembers).map(([uid, memberRole]) => ({
    uid,
    role: memberRole,
    ...(memberDetails[uid] || { displayName: uid, email: '' }),
  }));

  // Exclude emails that already belong to a current member
  const memberEmails = new Set(
    Object.values(memberDetails).map(d => (d.email || '').toLowerCase())
  );
  const pending = Object.entries(pendingInvites || {})
    .filter(([email]) => email.includes('@') && !memberEmails.has(email.toLowerCase()))
    .map(([email, roleEntry]) => ({
      email,
      role: typeof roleEntry === 'string' ? roleEntry : roleEntry.role,
    }));

  // navigator.clipboard needs a secure context and is absent in a few older
  // browsers, so fall back to a hidden textarea rather than failing silently.
  const copy = async (text, which) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(which);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      setCopied('');
    }
  };

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setFeedback({ type: 'err', text: 'Please enter a valid email address.' });
      return;
    }
    if (isOwner && role === 'contributor' && !linkedPersonId) {
      setFeedback({ type: 'err', text: 'Please select which person in the tree this contributor corresponds to.' });
      return;
    }
    setSending(true);
    setFeedback(null);
    // Non-owners always invite as viewer
    const effectiveRole = isOwner ? role : 'viewer';
    try {
      const result = await inviteMember(trimmed, effectiveRole, effectiveRole === 'contributor' ? linkedPersonId : null);
      setEmail('');
      setLinkedPersonId('');
      // Back to the least privilege on every send. Leaving the selector where
      // it was made the *next* invite inherit the last one's role silently —
      // invite one person as Admin and everyone after them is an Admin too
      // unless you notice. The form also sits directly above a "Current
      // Members" heading, so a sticky "Admin" reads as a description of the
      // members below it rather than a setting for the next invite.
      setRole('viewer');
      const roleLabel = effectiveRole === 'admin' ? 'admin (full edit access)' : effectiveRole === 'contributor' ? 'contributor (can edit your direct family)' : 'viewer (read-only)';
      const subject = `You've been invited to view ${treeName}`;

      // The link carries a single-use token that does the joining. It replaced
      // four manual steps — visit the site, sign up with one exact address,
      // sign in, find the Accept banner — of which the exact-address
      // requirement was the one that silently stranded people. Anyone opening
      // this link can accept with whatever account they already have.
      const link = buildInviteUrl(result?.token);

      const body =
        `Hi,\n\nI'd like to share our family tree with you on Kinship.\n\n` +
        `You've been added as a ${roleLabel} to ${treeName}.\n\n` +
        `Open this link and it will be waiting for you:\n${link}\n\n` +
        `You can sign in with any email address — the link knows who you are.` +
        (result?.token ? ` It works once, and expires in two weeks.` : '') +
        `\n\nLooking forward to sharing our family history with you!`;

      // Kept in state so the message is recoverable when the mail client never
      // appears — see the copy panel below.
      setLastInvite({ email: trimmed, subject, body });

      // location.href rather than window.open. This runs after `await
      // inviteMember`, so the click's user activation is already spent, and
      // Safari and Firefox block window.open from an async continuation — the
      // invitation would silently fail to open a mail client. A navigation to a
      // mailto: is never popup-blocked, and leaves no blank tab behind.
      // Named for what is actually observable. Nothing reports back from a
      // mailto:, so this measures that an invitation was created and the
      // browser was handed the message — not that anyone received it. The
      // invitee's address is deliberately absent: `track` would drop it anyway.
      track('invite_mailto_opened', { tree_people_count: peopleCount, role: effectiveRole });

      window.location.href =
        `mailto:${encodeURIComponent(trimmed)}` +
        `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      // Deliberately not "your email app has opened" — nothing reports back
      // from a mailto:, so claiming it opened is a guess, and a wrong one for
      // anyone on webmail with no mail handler configured.
      // Not "access granted" — nothing is granted yet. The invitation is
      // pending until they sign in with this exact address and press Accept in
      // InviteNotification, so saying otherwise leaves the inviter believing
      // the job is finished when the recipient has not even been told.
      setFeedback({ type: 'ok', text: `Invitation created for ${trimmed}. They'll get access once they sign in with that address and accept it. If your email app didn't open, copy the message below and send it yourself.` });
    } catch (e) {
      // The API returns actionable reasons — "Verify your email address before
      // inviting others", "That person is already a member of this tree",
      // "Invitation limit reached". The blanket "please try again" hid all of
      // them behind advice that could never work, since retrying without
      // addressing the cause fails identically.
      setFeedback({
        type: 'err',
        text: e?.message || 'Could not create the invitation. Please try again.',
      });
    }
    setSending(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)',
      zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: 520, maxHeight: '85vh', background: '#FDFBF7', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
        fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#F5EDE3',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif", color: '#2C2418', fontWeight: 700 }}>
              {isOwner ? 'Manage Access' : 'Invite Someone'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8B7D6B' }}>
              {isOwner ? 'Invite family members to view or edit this tree' : 'Invite someone to view this tree as a read-only viewer'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B' }}>
            <Icons.Close />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Invite section */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #E8E0D4' }}>
            <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#6B5E4F', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Invite by Email
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleInvite()}
                placeholder="family@example.com"
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 8,
                  border: '1.5px solid #D5CAB8', background: '#fff',
                  fontSize: 13, color: '#2C2418', outline: 'none',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
              {isOwner && (
                <select
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  style={{
                    padding: '9px 12px', borderRadius: 8,
                    border: '1.5px solid #D5CAB8', background: '#fff',
                    fontSize: 13, color: '#2C2418', outline: 'none',
                    fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
                  }}
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              )}
              <button
                onClick={handleInvite}
                disabled={sending}
                style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: sending ? '#B8AFA0' : '#3B2F1E',
                  color: '#F5EDE3', fontSize: 13, fontWeight: 600,
                  cursor: sending ? 'wait' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                }}
              >
                {sending ? 'Sending…' : 'Send Invite'}
              </button>
            </div>

            {/* Role descriptions — owner only */}
            {isOwner && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: role === 'contributor' ? 10 : 0 }}>
                  {ROLES.map(r => (
                    <div key={r.value} style={{
                      flex: 1, padding: '8px 10px', borderRadius: 8,
                      background: role === r.value ? '#F5EDE3' : '#FAF7F2',
                      border: `1.5px solid ${role === r.value ? '#C4975A' : '#E8E0D4'}`,
                      cursor: 'pointer',
                    }} onClick={() => setRole(r.value)}>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: role === r.value ? '#C4975A' : '#6B5E4F' }}>{r.label}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#8B7D6B' }}>{r.desc}</p>
                    </div>
                  ))}
                </div>

                {/* Linked person picker — only for contributor role */}
                {role === 'contributor' && (
                  <div style={{ marginTop: 2 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: '#6B5E4F', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 6 }}>
                      Which person in the tree are they?
                    </label>
                    <select
                      value={linkedPersonId}
                      onChange={e => setLinkedPersonId(e.target.value)}
                      style={{
                        width: '100%', padding: '9px 12px', borderRadius: 8,
                        border: '1.5px solid #D5CAB8', background: '#fff',
                        fontSize: 13, color: '#2C2418', outline: 'none',
                        fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
                      }}
                    >
                      <option value="">— Select person —</option>
                      {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <p style={{ margin: '5px 0 0', fontSize: 11, color: '#8B7D6B' }}>
                      This determines which family members they can edit: themselves, their spouse, their children (including a spouse's), their parents and their siblings. Nieces and nephews are not included.
                    </p>
                  </div>
                )}
              </>
            )}

            {feedback && (
              <div style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 8,
                background: feedback.type === 'ok' ? '#EBF7EC' : '#FDE8E8',
                color: feedback.type === 'ok' ? '#2E7D32' : '#C0392B',
                fontSize: 12,
              }}>
                {feedback.text}
              </div>
            )}

            {lastInvite && (
              <div style={{
                marginTop: 10, padding: '12px 14px', borderRadius: 8,
                background: '#F5EDE3', border: '1px solid #E8E0D4',
              }}>
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#6B5E4F' }}>
                  Message for <strong>{lastInvite.email}</strong> — paste it into
                  whichever email or messaging app you use.
                </p>
                <textarea
                  readOnly
                  value={`${lastInvite.subject}\n\n${lastInvite.body}`}
                  onFocus={e => e.target.select()}
                  style={{
                    width: '100%', height: 96, padding: '8px 10px', borderRadius: 6,
                    border: '1px solid #D5CAB8', background: '#fff', resize: 'vertical',
                    fontSize: 11, lineHeight: 1.5, color: '#2C2418',
                    fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => copy(`${lastInvite.subject}\n\n${lastInvite.body}`, 'message')}
                    style={{
                      padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                      border: '1.5px solid #D5CAB8', background: '#fff',
                      fontSize: 12, color: '#2C2418', fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {copied === 'message' ? 'Copied' : 'Copy message'}
                  </button>
                  <button
                    onClick={() => copy(lastInvite.email, 'email')}
                    style={{
                      padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                      border: '1.5px solid #D5CAB8', background: '#fff',
                      fontSize: 12, color: '#2C2418', fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    {copied === 'email' ? 'Copied' : 'Copy address'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Pending invites — owner only */}
          {isOwner && pending.length > 0 && (
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E8E0D4' }}>
              <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#6B5E4F', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Pending Invites ({pending.length})
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pending.map(p => (
                  <div key={p.email} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: 10,
                    background: '#FDF8F0', border: '1px dashed #D5CAB8',
                  }}>
                    {/* Avatar placeholder */}
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'rgba(196,151,90,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, flexShrink: 0,
                    }}>
                      ✉
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#2C2418', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.email}
                      </p>
                      <p style={{ margin: 0, fontSize: 11, color: '#A0937F' }}>Invite sent · hasn't signed in yet</p>
                    </div>

                    {/* Role badge */}
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                      background: 'rgba(196,151,90,0.12)', color: '#A07840',
                      textTransform: 'capitalize', flexShrink: 0,
                    }}>
                      {p.role}
                    </span>

                    {/* Revoke */}
                    <button
                      onClick={() => revokeInvite(p.email)}
                      title="Revoke invite"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#C0392B', padding: 4, display: 'flex',
                      }}
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current members — owner only */}
          {isOwner && <div style={{ padding: '20px 24px' }}>
            <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#6B5E4F', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Current Members ({members.length})
            </p>

            {members.length === 0 ? (
              <p style={{ fontSize: 13, color: '#A0937F', textAlign: 'center', padding: '16px 0' }}>
                No members yet. Invite family members above.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {members.map(m => (
                  <div key={m.uid} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 12px', borderRadius: 10,
                    background: '#FAF7F2', border: '1px solid #EBE4D8',
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'rgba(196,151,90,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#C4975A',
                      overflow: 'hidden', flexShrink: 0,
                    }}>
                      {m.photoURL
                        ? <img src={m.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : (m.displayName || m.email || '?').charAt(0).toUpperCase()
                      }
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#2C2418', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.displayName || m.email}
                      </p>
                      {m.email && m.displayName !== m.email && (
                        <p style={{ margin: 0, fontSize: 11, color: '#8B7D6B' }}>{m.email}</p>
                      )}
                    </div>

                    {/* Role picker + linked person (contributor only) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      <select
                        value={m.role}
                        onChange={e => updateMemberRole(m.uid, e.target.value, e.target.value !== 'contributor' ? null : undefined)}
                        style={{
                          padding: '5px 8px', borderRadius: 6,
                          border: '1.5px solid #D5CAB8', background: '#fff',
                          fontSize: 12, color: '#2C2418', outline: 'none',
                          fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
                        }}
                      >
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                      {m.role === 'contributor' && (
                        <select
                          value={m.linkedPersonId || ''}
                          onChange={e => updateMemberRole(m.uid, 'contributor', e.target.value || null)}
                          title="Linked person in tree"
                          style={{
                            padding: '4px 8px', borderRadius: 6,
                            border: '1.5px solid #D5CAB8', background: '#EEF4FB',
                            fontSize: 11, color: '#2C2418', outline: 'none',
                            fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', maxWidth: 130,
                          }}
                        >
                          <option value="">— Link to person —</option>
                          {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      )}
                    </div>

                    {/* Remove */}
                    <button
                      onClick={() => removeMember(m.uid)}
                      title="Remove member"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#C0392B', padding: 4, display: 'flex',
                      }}
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}
